import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box } from 'rebass/styled-components';
import { trackEvent } from 'helpers/analytics';
import { GrowListAgentHeader } from './GrowListAgentHeader';
import { ProfileDefinitionStep } from './ProfileDefinitionStep';
import { OutreachSetupStep } from './OutreachSetupStep';
import { LaunchPreviewStep } from './LaunchPreviewStep';
import { useGrowListAgent, STEP_INDICES } from './hooks/useGrowListAgent';
import { useGrowListEvents } from './hooks/useGrowListEvents';

export const GrowListAgentModal = ({ isOpen, onClose, sharedList, campaign }) => {
  const [isLaunching, setIsLaunching] = useState(false);

  const {
    currentStep,
    isWidgetLoading,
    widgetError,
    listName,
    campaignData,
    goToNextStep,
    handleClose,
    handleLaunch,
    updateCampaignData,
  } = useGrowListAgent({ sharedList, onClose, campaign });

  const handleCalibrationCompleted = useCallback(
    (payload) => {
      if (currentStep === STEP_INDICES.PROFILE_DEFINITION) {
        if (payload) {
          updateCampaignData({ profileDefinition: payload });
        }
        trackEvent('contacts:list:agent_setup_step_completed', {
          shared_list_id: sharedList?.id,
          list_name: sharedList?.name,
          step: 'profile_definition',
          step_index: 0,
        });
        goToNextStep();
      }
    },
    [currentStep, sharedList?.id, sharedList?.name, goToNextStep, updateCampaignData],
  );

  const handleOutreachCompleted = useCallback(
    (payload) => {
      if (currentStep === STEP_INDICES.OUTREACH_SETUP) {
        if (payload) {
          updateCampaignData({ outreachSetup: payload });
        }
        trackEvent('contacts:list:agent_setup_step_completed', {
          shared_list_id: sharedList?.id,
          list_name: sharedList?.name,
          step: 'outreach_setup',
          step_index: 1,
        });
        goToNextStep();
      }
    },
    [currentStep, sharedList?.id, sharedList?.name, goToNextStep, updateCampaignData],
  );

  useGrowListEvents({
    sharedList,
    onCalibrationCompleted: handleCalibrationCompleted,
    onOutreachCompleted: handleOutreachCompleted,
  });

  const handleLaunchClick = async () => {
    setIsLaunching(true);
    try {
      await handleLaunch();
      trackEvent('contacts:list:agent_setup_completed', {
        shared_list_id: sharedList?.id,
        list_name: sharedList?.name,
        campaign_id: campaign?.id,
      });
    } finally {
      setIsLaunching(false);
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case STEP_INDICES.PROFILE_DEFINITION:
        return <ProfileDefinitionStep isLoading={isWidgetLoading} error={widgetError} />;
      case STEP_INDICES.OUTREACH_SETUP:
        return <OutreachSetupStep isLoading={isWidgetLoading} error={widgetError} />;
      case STEP_INDICES.LAUNCH:
        return (
          <LaunchPreviewStep
            listName={listName}
            campaignData={campaignData}
            onLaunch={handleLaunchClick}
            isLaunching={isLaunching}
          />
        );
      default:
        return null;
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'white',
      }}
    >
      <GrowListAgentHeader listName={listName} onClose={handleClose} />
      <Box sx={{ flex: 1, overflow: 'hidden' }}>{renderCurrentStep()}</Box>
    </Box>
  );
};

GrowListAgentModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  sharedList: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    slug: PropTypes.string,
    collection: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
  }).isRequired,
  campaign: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
};

GrowListAgentModal.defaultProps = {
  campaign: null,
};

export default GrowListAgentModal;
